import { clone } from "lodash";
import * as React from "react";
import { Link } from "react-router-dom";
import * as AccountStore from "./AccountStore";
import HumanJson from "../human";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Avatar from "react-avatar";
import axios from "axios";

const { Component } = React;

interface ProfileScadsysDataState {
    currentUser: any;
    scadsysData: any;
    scadsysDataLoading: boolean;
    scadsysDataError: string | null;
    selectedOrganization: string;
}

export default class ProfileScadsysData extends Component<any, ProfileScadsysDataState> {
    constructor(props: any) {
        super(props);
        this.state = {
            currentUser: {},
            scadsysData: null,
            scadsysDataLoading: false,
            scadsysDataError: null,
            selectedOrganization: localStorage.getItem("organizationContext") || "",
        };
    }

    componentDidMount() {
        this.GetCurrentUser();
    }

    GetCurrentUser = () => {
        AccountStore.GetCurrentLoggedInUser(true).then((user) => {
            this.setState(
                {
                    currentUser: user,
                },
                () => {
                    // Auto-load Scadsys data after user is loaded
                    this.GetScadsysData();
                }
            );
        });
    };

    GetScadsysData = async () => {
        const { currentUser } = this.state;

        if (!currentUser.id) {
            this.setState({ scadsysDataError: "Missing user ID" });
            return;
        }

        this.setState({ scadsysDataLoading: true, scadsysDataError: null, scadsysData: null });

        try {
            const response = await axios.get("/api/users/getScadsysData", {
                params: {
                    userId: currentUser.id,
                },
            });

            if (response.data.success) {
                this.setState({
                    scadsysData: response.data.data,
                    scadsysDataLoading: false,
                });
            } else {
                this.setState({
                    scadsysDataError: "Failed to load Scadsys data: " + JSON.stringify(response.data.errors),
                    scadsysDataLoading: false,
                });
            }
        } catch (error: any) {
            this.setState({
                scadsysDataError: error.response?.data?.message || error.message || "An error occurred while loading Scadsys data",
                scadsysDataLoading: false,
            });
        }
    };

    renderScadsysDataSection = (title: string, icon: any, data: any, iconColor: string) => {
        if (!data || Object.keys(data).filter((key) => data[key] != null).length === 0) {
            return null;
        }

        return (
            <div className="mb-6 pb-6 border-b border-gray-200 last:border-0">
                <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                    <FontAwesomeIcon icon={icon} className={`mr-2 ${iconColor}`} />
                    {title}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                    {this.renderObjectEntries(data, 0)}
                </div>
            </div>
        );
    };

    renderObjectEntries = (data: any, depth: number = 0): React.ReactNode[] => {
        if (!data || typeof data !== 'object') {
            return [];
        }

        return Object.entries(data).map(([key, value]) => {
            if (value == null || value === "") return null;

            // Handle nested objects (recursively)
            if (typeof value === "object" && !Array.isArray(value)) {
                return (
                    <div key={key} className="col-span-2">
                        <div className={`font-medium text-gray-700 text-sm mb-2 ${depth > 0 ? 'mt-2' : ''}`}>
                            {this.formatKey(key)}:
                        </div>
                        <div className="pl-4 border-l-2 border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {this.renderObjectEntries(value, depth + 1)}
                            </div>
                        </div>
                    </div>
                );
            }

            // Handle arrays
            if (Array.isArray(value)) {
                if (value.length === 0) return null;
                
                return (
                    <div key={key} className="col-span-2">
                        <div className="font-medium text-gray-700 text-sm mb-2">{this.formatKey(key)}:</div>
                        <div className="pl-4 space-y-2">
                            {value.map((item, index) => (
                                <div key={index} className="p-3 bg-gray-50 rounded border border-gray-200">
                                    {typeof item === "object" ? (
                                        <div className="space-y-1">
                                            {this.renderObjectEntries(item, depth + 1)}
                                        </div>
                                    ) : (
                                        <span className="text-sm text-gray-900">{String(item)}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            }

            // Handle simple values
            return (
                <div key={key} className={depth > 0 ? "col-span-2" : ""}>
                    <div className="text-xs text-gray-500 mb-1">{this.formatKey(key)}</div>
                    <div className="text-sm text-gray-900 break-words">{String(value)}</div>
                </div>
            );
        }).filter(Boolean) as React.ReactNode[];
    };

    formatKey = (key: string): string => {
        // Convert camelCase to Title Case with spaces
        return key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    };

    render() {
        const { scadsysData, scadsysDataLoading, scadsysDataError, currentUser } = this.state;

        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center gap-4">
                        <Avatar round={true} size="70" name={currentUser.fullName} src={currentUser.photo} />
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">
                                {currentUser.firstName} {currentUser.lastName}
                            </h1>
                            <h2 className="text-xl text-gray-600">{currentUser.email}</h2>
                            <p className="text-sm text-gray-500">{currentUser.userName}</p>
                        </div>
                    </div>
                </div>

                {/* Navigation Tabs */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                    <nav className="flex space-x-4 px-6 py-3 border-b border-gray-200">
                        <Link to="/profile" className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                            <FontAwesomeIcon icon={["fad", "user"]} className="mr-2" />
                            Profile
                        </Link>
                        <Link to="/profile/data" className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                            <FontAwesomeIcon icon={["fad", "database"]} className="mr-2" />
                            Data
                        </Link>
                        <Link to="/profile/scadsys" className="px-3 py-2 text-sm font-medium rounded-md text-blue-600 bg-blue-50 border-b-2 border-blue-600">
                            <FontAwesomeIcon icon={["fad", "server"]} className="mr-2" />
                            Scadsys Data
                        </Link>
                        <Link to="/profile/mfa" className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                            <FontAwesomeIcon icon={["fad", "shield-check"]} className="mr-2" />
                            MFA
                        </Link>
                        <Link to="/profile/security" className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                            <FontAwesomeIcon icon={["fad", "lock"]} className="mr-2" />
                            Security
                        </Link>
                        <Link to="/profile/notifications" className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                            <FontAwesomeIcon icon={["fad", "bell"]} className="mr-2" />
                            Notifications
                        </Link>
                    </nav>
                </div>

                {/* Scadsys Data Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <FontAwesomeIcon icon={["fad", "server"]} className="mr-2 text-green-500" />
                            Scadsys Data
                        </h3>
                        <button
                            onClick={this.GetScadsysData}
                            disabled={scadsysDataLoading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                        >
                            {scadsysDataLoading ? (
                                <>
                                    <FontAwesomeIcon icon={["fad", "spinner"]} spin className="mr-2" />
                                    Loading...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={["fad", "sync"]} className="mr-2" />
                                    Refresh
                                </>
                            )}
                        </button>
                    </div>

                    {scadsysDataLoading && (
                        <div className="text-center py-12">
                            <FontAwesomeIcon icon={["fad", "spinner"]} spin size="3x" className="text-blue-500 mb-4" />
                            <p className="text-gray-600">Loading Scadsys data...</p>
                        </div>
                    )}

                    {scadsysDataError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                            <div className="flex items-start">
                                <FontAwesomeIcon icon={["fad", "exclamation-circle"]} className="text-red-500 mt-0.5 mr-3" />
                                <div>
                                    <h4 className="text-red-800 font-semibold mb-1">Error Loading Data</h4>
                                    <p className="text-red-700 text-sm">{scadsysDataError}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {!scadsysDataLoading && !scadsysDataError && !scadsysData && (
                        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                            <FontAwesomeIcon icon={["fad", "database"]} size="3x" className="text-gray-400 mb-4" />
                            <p className="text-gray-600 mb-2">No Scadsys data loaded yet</p>
                            <button onClick={this.GetScadsysData} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium">
                                Load Scadsys Data
                            </button>
                        </div>
                    )}

                    {scadsysData && (
                        <div className="space-y-4">
                            {this.renderScadsysDataSection("Personal Information", ["fad", "user"], scadsysData.personal, "text-blue-500")}
                            {this.renderScadsysDataSection("Employment Overview", ["fad", "briefcase"], scadsysData.employmentOverview, "text-purple-500")}
                            {this.renderScadsysDataSection("Emergency Contacts", ["fad", "phone"], scadsysData.emergencyContactOverview, "text-red-500")}
                            {this.renderScadsysDataSection("Medical Overview", ["fad", "heartbeat"], scadsysData.medicalOverview, "text-green-500")}

                            {/* Show raw JSON at the bottom for debugging */}
                            <details className="mt-6 pt-6 border-t border-gray-200">
                                <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900 font-medium">
                                    <FontAwesomeIcon icon={["fad", "code"]} className="mr-2" />
                                    View Raw Data (JSON)
                                </summary>
                                <pre className="mt-4 p-4 bg-gray-900 text-green-400 rounded-lg overflow-x-auto text-xs">{JSON.stringify(scadsysData, null, 2)}</pre>
                            </details>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}
